import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { availabilityAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PressureByType } from "../components/PressureByType";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";
import {
  Truck, User, Search, RefreshCw, Calendar, AlertTriangle,
  CheckCircle, XCircle, Wrench, ChevronDown, ChevronUp, Clock, Filter
} from "lucide-react";

const STATUS_CONFIG = {
  disponible: { label: "Disponible", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle, dot: "bg-emerald-500" },
  ocupado:    { label: "Ocupado",    color: "bg-red-100 text-red-800 border-red-300",         icon: XCircle,    dot: "bg-red-500" },
  mantenimiento: { label: "En Mantenimiento", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Wrench, dot: "bg-amber-500" },
};

const VEHICLE_TYPES = [
  { value: "", label: "Todos los tipos" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "van", label: "Van" },
  { value: "sprinter", label: "Sprinter" },
  { value: "autobus", label: "Autobús" },
];

function formatDT(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) + " " +
           d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  } catch { return dt.slice(0, 16).replace("T", " "); }
}

/**
 * Returns an active contract conflict that currently occupies the resource,
 * but ONLY if it has a reliable (real) end_datetime in the future.
 * Returns null if no such conflict exists — no guessing, no inferences.
 */
function getActiveReliableContract(resource, now) {
  if (!resource || resource.status !== "ocupado") return null;
  const conflicts = resource.conflicts || [];
  const nowMs = now.getTime();
  let best = null;
  for (const c of conflicts) {
    if (c.type !== "CONTRACT") continue;
    if (c.has_real_end_date !== true) continue;
    if (!c.start_date || !c.end_date) continue;
    const startMs = new Date(c.start_date).getTime();
    const endMs = new Date(c.end_date).getTime();
    if (isNaN(startMs) || isNaN(endMs)) continue;
    // Contract must be active NOW and end in the future
    if (startMs <= nowMs && nowMs < endMs) {
      if (!best || endMs < new Date(best.end_date).getTime()) {
        best = c;
      }
    }
  }
  return best;
}

/**
 * Format a positive millisecond diff as "HH:MM" or "Xd HH:MM" when > 24h.
 */
function formatRemaining(diffMs) {
  if (diffMs <= 0) return null;
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return days > 0 ? `${days}d ${hh}:${mm}` : `${hh}:${mm}`;
}

function ConflictDetail({ conflicts }) {
  if (!conflicts || conflicts.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {conflicts.map((c, i) => (
        <div key={i} className={cn(
          "flex items-start gap-2 text-xs p-2 rounded-md",
          c.type === "MAINTENANCE" ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800"
        )}>
          {c.type === "MAINTENANCE" ? (
            <Wrench className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          )}
          <div>
            {c.type === "MAINTENANCE" ? (
              <span className="font-medium">{c.reason} ({c.start_date} — {c.end_date})</span>
            ) : (
              <>
                <span className="font-semibold">{c.contract_folio}</span>
                <span className="ml-1">— {c.client_name}</span>
                <div className="text-[11px] mt-0.5 opacity-75">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatDT(c.start_date)} → {formatDT(c.end_date)}
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResourceRow({ resource, type, now }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CONFIG[resource.status] || STATUS_CONFIG.disponible;
  const Icon = cfg.icon;
  const hasConflicts = resource.conflicts && resource.conflicts.length > 0;

  // Timer: only if resource is occupied AND has an active contract with a reliable future end_datetime
  const activeContract = getActiveReliableContract(resource, now);
  const remainingLabel = activeContract
    ? formatRemaining(new Date(activeContract.end_date).getTime() - now.getTime())
    : null;

  return (
    <div
      className={cn(
        "border rounded-lg transition-all",
        hasConflicts ? "border-l-4" : "",
        resource.status === "ocupado" ? "border-l-red-500" :
        resource.status === "mantenimiento" ? "border-l-amber-500" : ""
      )}
      data-testid={`resource-row-${resource.id}`}
    >
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
        onClick={() => hasConflicts && setOpen(!open)}
      >
        <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", cfg.dot)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-slate-900 truncate">{resource.name}</span>
            {type === "vehicle" && resource.plate && (
              <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{resource.plate}</span>
            )}
            {type === "vehicle" && resource.type && (
              <span className="text-xs text-slate-400 capitalize">{resource.type}</span>
            )}
            {type === "driver" && resource.phone && (
              <span className="text-xs text-slate-500">{resource.phone}</span>
            )}
          </div>
        </div>
        {remainingLabel && (
          <Badge
            variant="outline"
            className="text-xs px-2 py-0.5 border bg-sky-50 text-sky-800 border-sky-300"
            data-testid={`availability-timer-${resource.id}`}
            title={`Se libera el ${formatDT(activeContract.end_date)}`}
          >
            <Clock className="w-3 h-3 mr-1" />
            Disponible en {remainingLabel}
          </Badge>
        )}
        <Badge variant="outline" className={cn("text-xs px-2 py-0.5 border", cfg.color)}>
          <Icon className="w-3 h-3 mr-1" />
          {cfg.label}
        </Badge>
        {hasConflicts && (
          <Badge variant="secondary" className="text-xs">{resource.conflicts.length}</Badge>
        )}
        {hasConflicts && (
          open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </div>
      {open && hasConflicts && (
        <div className="px-3 pb-3 border-t border-dashed">
          <ConflictDetail conflicts={resource.conflicts} />
        </div>
      )}
    </div>
  );
}

export default function Disponibilidad() {
  const [tab, setTab] = useState("vehicles");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ vehicles: [], drivers: [] });
  const [search, setSearch] = useState("");
  const [vehicleType, setVehicleType] = useState("");

  // Default: today + 7 days
  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(fmt(today));
  const [endDate, setEndDate] = useState(fmt(nextWeek));

  // Live clock for countdown timers (1 tick per minute). Cleaned up on unmount.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_datetime: `${startDate}T00:00:00`,
        end_datetime: `${endDate}T23:59:59`,
      };
      if (vehicleType) params.vehicle_type = vehicleType;
      if (search) params.search = search;

      const res = await availabilityAPI.dashboard(params);
      setData(res.data);
    } catch (err) {
      console.error("Error loading availability:", err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, vehicleType, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const vehicles = data.vehicles || [];
  const drivers = data.drivers || [];

  const vAvail = vehicles.filter(v => v.status === "disponible").length;
  const vOccupied = vehicles.filter(v => v.status === "ocupado").length;
  const vMaint = vehicles.filter(v => v.status === "mantenimiento").length;
  const dAvail = drivers.filter(d => d.status === "disponible").length;
  const dOccupied = drivers.filter(d => d.status === "ocupado").length;

  const currentList = tab === "vehicles" ? vehicles : drivers;

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-5" data-testid="availability-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Disponibilidad Operativa</h1>
            <p className="text-slate-500 mt-1 text-sm">Consulta y valida disponibilidad de unidades y choferes</p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2" data-testid="refresh-availability">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Fecha inicio</label>
                <Input
                  type="date" value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Fecha fin</label>
                <Input
                  type="date" value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-end-date"
                />
              </div>
              {tab === "vehicles" && (
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Tipo de unidad</label>
                  <select
                    value={vehicleType}
                    onChange={e => setVehicleType(e.target.value)}
                    className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="filter-vehicle-type"
                  >
                    {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              )}
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={tab === "vehicles" ? "Nombre, placa, marca..." : "Nombre del chofer..."}
                    className="pl-8"
                    data-testid="filter-search"
                  />
                </div>
              </div>
              <Button onClick={fetchData} disabled={loading} className="gap-2 bg-slate-800 hover:bg-slate-700" data-testid="apply-filters">
                <Filter className="w-4 h-4" />
                Consultar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="card-hover bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-emerald-700 font-medium">Unidades Disponibles</p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="vehicles-available">{vAvail}</p>
            </CardContent>
          </Card>
          <Card className="card-hover bg-gradient-to-br from-red-50 to-rose-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-red-700 font-medium">Unidades Ocupadas</p>
              <p className="text-2xl font-bold text-red-700" data-testid="vehicles-occupied">{vOccupied}</p>
            </CardContent>
          </Card>
          <Card className="card-hover bg-gradient-to-br from-amber-50 to-yellow-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-amber-700 font-medium">En Mantenimiento</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="vehicles-maintenance">{vMaint}</p>
            </CardContent>
          </Card>
          <Card className="card-hover bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-emerald-700 font-medium">Choferes Disponibles</p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="drivers-available">{dAvail}</p>
            </CardContent>
          </Card>
          <Card className="card-hover bg-gradient-to-br from-red-50 to-rose-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-red-700 font-medium">Choferes Ocupados</p>
              <p className="text-2xl font-bold text-red-700" data-testid="drivers-occupied">{dOccupied}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setTab("vehicles")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all",
              tab === "vehicles" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
            )}
            data-testid="tab-vehicles"
          >
            <Truck className="w-4 h-4" />
            Unidades ({vehicles.length})
          </button>
          <button
            onClick={() => setTab("drivers")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all",
              tab === "drivers" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
            )}
            data-testid="tab-drivers"
          >
            <User className="w-4 h-4" />
            Choferes ({drivers.length})
          </button>
        </div>

        {/* List */}
        <div className="space-y-2" data-testid="resource-list">
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Consultando disponibilidad...
            </div>
          ) : currentList.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Calendar className="w-8 h-8 mx-auto mb-2" />
              No se encontraron recursos
            </div>
          ) : (
            currentList.map(item => (
              <ResourceRow key={item.id} resource={item} type={tab === "vehicles" ? "vehicle" : "driver"} now={now} />
            ))
          )}
        </div>
        {/* v4.58.0: Presión por tipo de unidad (RBAC: admin/admin_vendedor/logistica/vendedor_logistica) */}
        <PressureGate />
      </div>
    </Layout>
  );
}

// v4.58.0: Gate de RBAC — vendedor puro y mantenimiento no ven la sección
const PressureGate = () => {
  const { user } = useAuth();
  const allowed = ["admin", "admin_vendedor", "logistica", "vendedor_logistica"];
  if (!user || !allowed.includes(user.role)) return null;
  return <PressureByType />;
};
